import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Video, CheckCircle, ArrowRight, User, Envelope, Phone, ChatText, Users, PlayCircle, ShieldCheck, SealCheck, CalendarCheck, CircleNotch } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Layout } from '../components/layout/Layout';
import { API_URL, formatApiErrorDetail } from '../lib/utils';
import { LiquidGlassBg } from '../components/ui/LiquidGlassComponents';

const topics = [
  'General Platform Overview',
  'Insurance Rail Demo',
  'Wallet Pool Management Demo',
  'Reimbursement Rail Demo',
  'Calculators Demo',
  'Admin Panel Demo',
  'Custom Requirements Discussion'
];

const InputWrapper = ({ label, icon: Icon, children }) => (
  <div className="space-y-2">
    <Label className="text-[10px] font-black text-white/30 uppercase tracking-widest ml-1">{label}</Label>
    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#0048ff] transition-colors">
        <Icon size={20} weight="duotone" />
      </div>
      {children}
    </div>
  </div>
);

export default function DemoPage() {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    employees: '',
    topic: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: formData.company_name,
          contact_name: formData.contact_name,
          email: formData.email,
          phone: formData.phone,
          employees: parseInt(formData.employees) || null,
          source: 'demo',
          industry: formData.topic,
          calculator_results: formData.message ? { message: formData.message } : null,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(formatApiErrorDetail(err.detail));
      }

      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  if (success) {
    return (
      <Layout>
        <section className="min-h-screen bg-[#030712] relative overflow-hidden flex items-center pt-20">
          <LiquidGlassBg />
          <div className="container-wide relative z-10 px-4">
            <motion.div 
              className="max-w-xl mx-auto glass-panel p-12 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="w-24 h-24 rounded-3xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center mx-auto mb-8 animate-pulse">
                <CalendarCheck size={56} className="text-violet-500" weight="duotone" />
              </div>
              <h1 className="text-4xl font-black text-white mb-6 tracking-tight uppercase">Audit Scheduled</h1>
              <p className="text-white/40 text-lg mb-10 leading-relaxed">
                Your request for a structural demonstration has been received. Our chief architects will prepare a personalized portal preview and contact you at <span className="text-white font-bold">{formData.email}</span>.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/calculators/quick">
                  <Button className="h-14 px-8 rounded-2xl bg-[#0048ff] hover:bg-[#003bce] text-white font-black uppercase tracking-widest text-xs gap-3">
                    Explore Calculators <ArrowRight weight="bold" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="h-14 px-8 rounded-2xl border-white/10 bg-white/5 text-white font-black uppercase tracking-widest text-xs">
                    Return to Portal
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="min-h-screen bg-[#030712] relative overflow-hidden pt-32 pb-20">
        <LiquidGlassBg />
        
        <div className="container-wide relative z-10 px-4 md:px-8">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-16">
            {/* Left Side: Content */}
            <div className="flex-1 space-y-12">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0048ff]/10 text-[#0048ff] text-xs font-bold uppercase tracking-widest mb-4">
                  <PlayCircle size={16} weight="bold" /> Strategic Session
                </div>
                <h1 className="text-5xl md:text-7xl font-black text-white leading-tight tracking-tight">
                  Request a <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0048ff] to-[#00d2ff]">Live Demo</span>
                </h1>
                <p className="text-white/40 text-xl leading-relaxed max-w-xl">
                  Witness the capabilities of our multi-rail healthcare infrastructure firsthand. See how real-time governance and automated settlement can transform your organization.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  { icon: ShieldCheck, title: "Risk Mitigation", desc: "See our actuarial safeguard protocols in action." },
                  { icon: Video, title: "Feature Deep-Dive", desc: "Granular overview of wallet and claim rails." },
                  { icon: CheckCircle, title: "Governance UI", desc: "Experience the commander's view of your health data." },
                  { icon: Users, title: "Employee Portals", desc: "Preview the mobile-first employee experience." }
                ].map((feature, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/5 transition-colors group">
                    <div className="w-12 h-12 rounded-xl bg-[#0048ff]/10 border border-[#0048ff]/20 flex items-center justify-center text-[#0048ff] group-hover:scale-110 transition-transform">
                      <feature.icon size={24} weight="duotone" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold tracking-wide">{feature.title}</h4>
                      <p className="text-white/30 text-xs mt-1">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side: Form */}
            <div className="w-full lg:max-w-lg">
              <motion.div 
                className="glass-panel p-8 md:p-10 relative"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputWrapper label="Full Name" icon={User}>
                      <Input
                        required
                        value={formData.contact_name}
                        onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                        className="h-14 pl-12 bg-white/5 border-white/10 focus:border-[#0048ff] text-white rounded-xl placeholder:text-white/20"
                        placeholder="John Doe"
                      />
                    </InputWrapper>
                    <InputWrapper label="Corporate Email" icon={Envelope}>
                      <Input
                        required
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="h-14 pl-12 bg-white/5 border-white/10 focus:border-[#0048ff] text-white rounded-xl placeholder:text-white/20"
                        placeholder="john@company.com"
                      />
                    </InputWrapper>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputWrapper label="Company Name" icon={Video}>
                      <Input
                        required
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                        className="h-14 pl-12 bg-white/5 border-white/10 focus:border-[#0048ff] text-white rounded-xl placeholder:text-white/20"
                        placeholder="Organization"
                      />
                    </InputWrapper>
                    <InputWrapper label="Contact Number" icon={Phone}>
                      <Input
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="h-14 pl-12 bg-white/5 border-white/10 focus:border-[#0048ff] text-white rounded-xl placeholder:text-white/20"
                        placeholder="+92 XXX XXXXXXX"
                      />
                    </InputWrapper>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputWrapper label="Demo Priority Topic" icon={ChatText}>
                      <Select 
                        value={formData.topic} 
                        onValueChange={(v) => setFormData({...formData, topic: v})}
                      >
                        <SelectTrigger className="h-14 pl-12 bg-white/5 border-white/10 focus:ring-[#0048ff] text-white rounded-xl">
                          <SelectValue placeholder="Focus Area" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-white/10 text-white">
                          {topics.map(topic => (
                            <SelectItem key={topic} value={topic} className="hover:bg-white/5 focus:bg-white/5">{topic}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </InputWrapper>
                    <InputWrapper label="Scale of Operations" icon={Users}>
                      <Input
                        type="number"
                        placeholder="Active Lives"
                        value={formData.employees}
                        onChange={(e) => setFormData({...formData, employees: e.target.value})}
                        className="h-14 pl-12 bg-white/5 border-white/10 focus:border-[#0048ff] text-white rounded-xl placeholder:text-white/20"
                      />
                    </InputWrapper>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[10px] font-black text-white/30 uppercase tracking-widest ml-1">Special Requirements (Optional)</Label>
                    <Textarea
                      placeholder="e.g., We are specifically looking for OPD wallet management with split-billing..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="min-h-[100px] bg-white/5 border-white/10 focus:border-[#0048ff] text-white rounded-xl placeholder:text-white/20 resize-none p-4"
                    />
                  </div>

                  {error && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }} 
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-bold uppercase tracking-widest text-center"
                    >
                      {error}
                    </motion.div>
                  )}

                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full h-16 rounded-2xl bg-[#0048ff] hover:bg-[#003bce] text-white font-black uppercase tracking-[0.2em] text-sm shadow-[0_20px_50px_rgba(0,72,255,0.3)] transition-all flex gap-4"
                  >
                    {loading ? (
                      <>
                        <CircleNotch size={20} className="animate-spin" /> Scheduling Session...
                      </>
                    ) : (
                      <>
                        Request Corporate Portal Demo <ArrowRight weight="bold" />
                      </>
                    )}
                  </Button>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
